import { addDays } from '../lib/localtime';
import { place } from '../state';

const tz = () => place.peek().timeZone;

/** A time of day at the place. A missing time is for the caller to put in words ("No sunrise"): there is no stand-in. */
export function fmtTime(d: Date | number, seconds = false): string {
  const date = typeof d === 'number' ? new Date(d) : d;
  return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', second: seconds ? '2-digit' : undefined, timeZone: tz() }).format(date);
}

export function fmtDate(d: Date | number, withYear = false): string {
  const date = typeof d === 'number' ? new Date(d) : d;
  return new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: withYear ? 'numeric' : undefined, timeZone: tz() }).format(date);
}

export function fmtDay(d: Date | number): string {
  const date = typeof d === 'number' ? new Date(d) : d;
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', timeZone: tz() }).format(date);
}

/** Local calendar day key (YYYY-MM-DD) in the place's time zone. */
export function dayKey(d: Date | number): string {
  const date = typeof d === 'number' ? new Date(d) : d;
  return new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: tz() }).format(date);
}

export function fmtDateTime(d: Date | number): string {
  const date = typeof d === 'number' ? new Date(d) : d;
  const today = dayKey(new Date());
  const k = dayKey(date);
  const otherYear = k.slice(0, 4) !== today.slice(0, 4);
  // Tomorrow and yesterday are the next and last calendar dates, not 24 hours on or back (the day the clocks go
  // back lasts 25 hours).
  const day = k === today ? 'Today' : k === addDays(today, 1) ? 'Tomorrow' : k === addDays(today, -1) ? 'Yesterday' : fmtDate(date, otherYear);
  return `${day} ${fmtTime(date)}`;
}

/** "YYYY-MM-DDTHH:mm" wall-clock time at the place (for datetime-local inputs). */
export function toPlaceInput(d: Date): string {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: tz() })
      .formatToParts(d)
      .map((p) => [p.type, p.value]),
  );
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

/** The instant a wall-clock time at the place refers to (inverse of toPlaceInput). */
export function fromPlaceInput(v: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(v);
  if (!m) return null;
  const wall = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  let t = wall;
  // Two passes settle the time-zone offset, including across clock changes.
  for (let i = 0; i < 2; i++) {
    const shown = fromInputUtc(toPlaceInput(new Date(t)));
    t += wall - shown;
  }
  return new Date(t);
}
const fromInputUtc = (v: string) => {
  const [d, h] = v.split('T');
  const [y, mo, da] = d.split('-').map(Number);
  const [hh, mm] = h.split(':').map(Number);
  return Date.UTC(y, mo - 1, da, hh, mm);
};

export function fmtRelative(t: number, now = Date.now()): string {
  const s = Math.round((t - now) / 1000);
  const a = Math.abs(s);
  let txt: string;
  if (a < 45) txt = 'moments';
  else if (a < 90 * 60) txt = `${Math.round(a / 60)} min`;
  else if (a < 36 * 3600) txt = `${Math.round(a / 3600)} h`;
  else txt = `${Math.round(a / 86400)} days`;
  if (txt === 'moments') return s >= 0 ? 'in a moment' : 'just now';
  return s >= 0 ? `in ${txt}` : `${txt} ago`;
}

const POINTS16 = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
const WORDS8 = ['north', 'north-east', 'east', 'south-east', 'south', 'south-west', 'west', 'north-west'];

export const compassPoint = (az: number) => POINTS16[Math.round((((az % 360) + 360) % 360) / 22.5) % 16];
export const directionWords = (az: number) => WORDS8[Math.round((((az % 360) + 360) % 360) / 45) % 8];

export function altitudeWords(alt: number): string {
  if (alt < -0.5) return 'below the horizon';
  if (alt < 10) return 'very low';
  if (alt < 25) return 'low';
  if (alt < 50) return 'halfway up';
  if (alt < 75) return 'high';
  return 'almost overhead';
}

export function whereInSky(alt: number, az: number): string {
  if (alt < -0.5) return `Below the horizon, ${directionWords(az)}`;
  if (alt > 80) return 'Almost straight overhead';
  return `${cap(altitudeWords(alt))} in the ${directionWords(az)}`;
}

export const cap = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);

export function fmtNum(n: number, digits = 0): string {
  return n.toLocaleString('en-GB', { maximumFractionDigits: digits, minimumFractionDigits: digits });
}

export function fmtLightYears(ly: number): string {
  if (!ly) return 'unknown';
  if (ly < 100) return `${fmtNum(ly, ly < 10 ? 1 : 0)} light years`;
  return `${fmtNum(Math.round(ly / (ly < 1000 ? 1 : 10)) * (ly < 1000 ? 1 : 10))} light years`;
}

export function fmtKm(km: number): string {
  if (km < 10) return `${fmtNum(km, 1)} km`;
  if (km < 1e6) return `${fmtNum(Math.round(km))} km`;
  if (km < 1e9) return `${fmtNum(km / 1e6, 1)} million km`;
  return `${fmtNum(km / 1e9, 2)} billion km`;
}

export function fmtMag(m: number): string {
  return (m < 0 ? '−' : '') + Math.abs(m).toFixed(1);
}

export function brightnessWords(m: number): string {
  if (m < -3) return 'brilliant';
  if (m < -1) return 'very bright';
  if (m < 1) return 'bright';
  if (m < 2.5) return 'easy to see';
  if (m < 4) return 'visible from towns';
  if (m < 5.5) return 'needs a darker sky';
  if (m < 7) return 'binoculars';
  return 'telescope';
}

export function fmtAngle(deg: number): string {
  const a = Math.abs(deg);
  if (a >= 1) return `${deg.toFixed(1)}°`;
  if (a * 60 >= 1) return `${(deg * 60).toFixed(1)}′`;
  return `${(deg * 3600).toFixed(1)}″`;
}
